import {
  TRAVEL_IDENTITY_GENERIC_MEMBER,
  memberIdentityShortTag,
  travelMemberProductDisplayName,
} from '../member-identity/memberIdentityPresentation';

export const MEMBER_SUMMARY_BATCH_LIMIT = 100;
export const MEMBER_FALLBACK_WITHOUT_ID = TRAVEL_IDENTITY_GENERIC_MEMBER;

export function collectMemberIds(values) {
  const seen = new Set();
  const ids = [];
  (Array.isArray(values) ? values : []).forEach((value) => {
    if (value == null || value === '') return;
    const id = String(value).trim();
    if (!id || id === 'undefined' || id === 'null') return;
    if (seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

export function chunkMemberIds(ids, limit = MEMBER_SUMMARY_BATCH_LIMIT) {
  const source = Array.isArray(ids) ? ids : [];
  const size = Number(limit) > 0 ? Number(limit) : MEMBER_SUMMARY_BATCH_LIMIT;
  const chunks = [];
  for (let index = 0; index < source.length; index += size) {
    chunks.push(source.slice(index, index + size));
  }
  return chunks;
}

export function indexMemberSummaries(rows) {
  const summaries = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = String(row?.memberId ?? '').trim();
    if (!id) return;
    summaries[id] = { ...row, memberId: id };
  });
  return summaries;
}

export function memberFallbackLabel(memberId) {
  const id = String(memberId ?? '').trim();
  if (!id) return MEMBER_FALLBACK_WITHOUT_ID;
  const tag = memberIdentityShortTag(id);
  return tag ? `${MEMBER_FALLBACK_WITHOUT_ID} ${tag}` : MEMBER_FALLBACK_WITHOUT_ID;
}

export function memberDisplayName(memberId, summariesById) {
  const id = String(memberId ?? '').trim();
  if (!id) return MEMBER_FALLBACK_WITHOUT_ID;
  const summary = summariesById?.[id];
  if (summary) {
    const hasIdentityField = [summary.nickname, summary.displayName, summary.username]
      .some((value) => String(value ?? '').trim());
    if (hasIdentityField) {
      return travelMemberProductDisplayName(summary, {
        genericFallback: MEMBER_FALLBACK_WITHOUT_ID,
      });
    }
  }
  return memberFallbackLabel(id);
}

export function memberDisplayNameWithId(memberId, summariesById) {
  const id = String(memberId ?? '').trim();
  if (!id) return MEMBER_FALLBACK_WITHOUT_ID;
  const fallback = memberFallbackLabel(id);
  const name = memberDisplayName(id, summariesById);
  if (name === fallback) return fallback;
  const tag = memberIdentityShortTag(id);
  return tag ? `${name}（${tag}）` : name;
}

export async function loadMemberSummaryIndex(memberIds, {
  batch,
  isCurrent = () => true,
  limit = MEMBER_SUMMARY_BATCH_LIMIT,
} = {}) {
  const ids = collectMemberIds(memberIds);
  if (!ids.length) return {};
  if (typeof batch !== 'function') return {};
  try {
    const rows = [];
    for (const chunk of chunkMemberIds(ids, limit)) {
      if (!isCurrent()) return null;
      const part = await batch(chunk);
      if (!isCurrent()) return null;
      if (Array.isArray(part)) rows.push(...part);
    }
    if (!isCurrent()) return null;
    return indexMemberSummaries(rows);
  } catch {
    if (!isCurrent()) return null;
    return {};
  }
}
